// BrewRating.jsx — the shared 👍/👎 + descriptor rating used in BOTH touchpoints
// (post-brew push tap and next-morning branch gate). Writes one check-in record
// through store.submitCheckIn, which folds it into the taste profile.
//
// Flow: 👍 submits immediately (would_repeat = true, no descriptors → Repeat).
//       👎 reveals descriptor chips (bitter/flat/strong/not-sweet/stomach + Other note) → submit
//       (would_repeat = false → Tweak). Descriptors map to taste[] which drives the
//       deterministic tweak rules + taste-profile bias.

import { useState } from "react";
import { motion } from "framer-motion";
import { Chip, PrimaryButton, SecondaryButton, Eyebrow } from "./pronto";
import { TASTE_DESCRIPTORS as DESCRIPTORS } from "../lib/coffee.js";

// rating (1-5 stars) inferred from the thumb so the existing profile math (which
// reads either would_repeat or rating) stays consistent.
export function BrewRating({ brewLog, beanName, onSubmit, compact = false }) {
  const [thumb, setThumb] = useState(null);      // true | false | null
  const [taste, setTaste] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setTaste(p => p.includes(id) ? p.filter(t => t !== id) : [...p, id]);

  const submit = async (wouldRepeat, tasteArr = [], notes = "") => {
    setBusy(true);
    try {
      await onSubmit({
        brew_log_id: brewLog?.id ?? null,
        bean_id: brewLog?.bean_id ?? null,
        rating: wouldRepeat ? 5 : 2,
        taste: tasteArr,
        would_repeat: wouldRepeat,
        notes,
      });
    } finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
      <Eyebrow className="mb-2">{compact ? "Rate this brew" : `How's the ${beanName || "coffee"}?`}</Eyebrow>

      {thumb !== false && (
        <div className="flex gap-2 mb-1">
          <button
            onClick={() => { setThumb(true); submit(true); }}
            disabled={busy}
            className="flex-1 py-3 rounded-[14px] bg-pronto-navy/10 text-pronto-navy font-semibold text-[15px] active:scale-[0.98] transition-transform"
          >👍 Loved it</button>
          <button
            onClick={() => setThumb(false)}
            disabled={busy}
            className="flex-1 py-3 rounded-[14px] bg-white/60 border border-pronto-text-4/40 text-pronto-ink font-medium text-[15px] active:scale-[0.98] transition-transform"
          >👎 Off</button>
        </div>
      )}

      {thumb === false && (
        <div className="mt-2">
          <div className="flex flex-wrap gap-2 mb-3">
            {DESCRIPTORS.map(d => (
              <Chip key={d.id} tone={taste.includes(d.id) ? "accent" : "rest"} onClick={() => toggle(d.id)}>
                {d.label}
              </Chip>
            ))}
          </div>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Other / a note (optional)"
            className="w-full mb-3 px-3 py-2.5 rounded-[12px] bg-white/70 border border-pronto-text-4/30 text-[14px] text-pronto-ink placeholder:text-pronto-text-4 outline-none focus:border-pronto-navy/40"
          />
          <div className="flex gap-2">
            <SecondaryButton onClick={() => setThumb(null)} disabled={busy}>Back</SecondaryButton>
            <PrimaryButton onClick={() => submit(false, taste, note)} disabled={busy}>
              {busy ? "Saving…" : "Adjust the recipe →"}
            </PrimaryButton>
          </div>
        </div>
      )}
    </motion.div>
  );
}
